import type { CpmPendingItem } from '../../../../types/centralProductMaster.types';

export interface PendingLiveAncestor {
    id: string;
    name: string;
    type: string;
}

export interface PendingChainNode {
    item: CpmPendingItem;
    depth: number;
}

export interface PendingChainCard {
    key: string;
    root: CpmPendingItem;
    nodes: PendingChainNode[];
    isGroup: boolean;
    liveAncestor: PendingLiveAncestor | null;
    chainLabel: string;
}

function levelParts(nodes: CpmPendingItem[]): string {
    const levels: Record<string, number> = {};
    for (const n of nodes) {
        const t = n.recordType?.toUpperCase?.() ?? n.level?.toUpperCase?.() ?? '';
        if (t === 'BRAND' || t === 'PRODUCT' || t === 'VARIANT') {
            levels[t] = (levels[t] ?? 0) + 1;
        }
    }
    return (['BRAND', 'PRODUCT', 'VARIANT'] as const)
        .filter((t) => levels[t])
        .map((t) => {
            const n = levels[t];
            const label = t.toLowerCase();
            return `${n} ${label}${n > 1 ? 's' : ''}`;
        })
        .join(' → ');
}

/**
 * Client-side Brand→Product→Variant grouping from parent refs on the current page.
 * Option D chain pagination keeps PENDING relatives of the queried levels on the
 * same page. Remaining 1-node cards are true Standalone (or Already-live when
 * the parent is ACTIVE and not in `items`).
 */
export function buildPendingChains(items: CpmPendingItem[]): {
    groupChains: PendingChainCard[];
    standalone: CpmPendingItem[];
} {
    const byId = new Map(items.map((item) => [item._id, item]));
    const children = new Map<string, CpmPendingItem[]>();
    const order: Record<string, number> = { brand: 0, product: 1, variant: 2 };

    for (const item of items) {
        const parentId = item.parent?.id;
        if (parentId && byId.has(parentId)) {
            const list = children.get(parentId) ?? [];
            list.push(item);
            children.set(parentId, list);
        }
    }

    for (const [, list] of children) {
        list.sort((a, b) => (order[a.level] ?? 9) - (order[b.level] ?? 9));
    }

    const collect = (node: CpmPendingItem, depth: number, acc: PendingChainNode[]) => {
        acc.push({ item: node, depth });
        for (const child of children.get(node._id) ?? []) {
            collect(child, depth + 1, acc);
        }
    };

    const roots = items.filter((item) => {
        const parentId = item.parent?.id;
        return !parentId || !byId.has(parentId);
    });

    const allCards: PendingChainCard[] = [];
    const visited = new Set<string>();

    for (const root of roots) {
        const nodes: PendingChainNode[] = [];
        collect(root, 0, nodes);
        nodes.forEach((n) => visited.add(n.item._id));

        const parent = root.parent;
        // "Already live" only when the missing parent is genuinely ACTIVE
        // (root.parentIsActive is backend-verified against Mongo status) —
        // a merely off-page PENDING parent must render as a plain chain,
        // not a false "Already live" badge.
        const liveAncestor =
            parent && parent.name && !(parent.id && byId.has(parent.id)) && root.parentIsActive
                ? {
                      id: parent.id,
                      name: parent.name,
                      type: parent.level || 'Brand',
                  }
                : null;

        const itemsOnly = nodes.map((n) => n.item);
        const isGroup = nodes.length > 1;
        const parts = levelParts(itemsOnly);
        const chainLabel = liveAncestor
            ? `Pending beneath this ${String(liveAncestor.type).toLowerCase()} · ${parts}`
            : `${nodes.length}-level chain · ${parts}`;

        allCards.push({
            key: root._id,
            root,
            nodes,
            isGroup,
            liveAncestor,
            chainLabel,
        });
    }

    for (const item of items) {
        if (!visited.has(item._id)) {
            allCards.push({
                key: item._id,
                root: item,
                nodes: [{ item, depth: 0 }],
                isGroup: false,
                liveAncestor: null,
                chainLabel: `1-level chain · ${levelParts([item])}`,
            });
        }
    }

    const groupChains = allCards.filter((c) => c.isGroup || !!c.liveAncestor);
    const standalone = allCards
        .filter((c) => !c.isGroup && !c.liveAncestor)
        .flatMap((c) => c.nodes.map((n) => n.item));

    return { groupChains, standalone };
}
